// What a purchase overlay shows, decided from what has arrived (pure, for
// the tests). The rule both follow: nothing that depends on the school's
// checkout settings — whether it asks for a sign-in, its name on the basket
// line — is assumed before the settings arrive, and settings that can't be
// had are an error to retry, never "no sign-in needed".

export type ConfigStatus = "loading" | "error" | "ready";

export type OffersView = "config-error" | "offers-error" | "loading" | "empty" | "offers";

/** A pass overlay's body. Buying waits on the settings too (`canBuy`). */
export function offersView({
  config,
  offers,
  offersFailed,
}: {
  config: ConfigStatus;
  offers: readonly unknown[] | null;
  offersFailed: boolean;
}): { view: OffersView; canBuy: boolean } {
  const canBuy = config === "ready";
  if (config === "error") return { view: "config-error", canBuy };
  if (offersFailed) return { view: "offers-error", canBuy };
  if (!offers) return { view: "loading", canBuy };
  return { view: offers.length === 0 ? "empty" : "offers", canBuy };
}

export type MembershipView = "config-error" | "loading" | "gated" | "plans-error" | "empty" | "plans";

/**
 * A membership overlay's body. The sign-in gate is decided only once the
 * settings and the customer's session are known; the plans show only once
 * they've arrived for the customer as they are now (a refetch for a newly
 * signed-in customer is "loading", so their own plan never offers Subscribe).
 */
export function membershipView({
  config,
  requireLogin,
  auth,
  plans,
  plansFailed,
}: {
  config: ConfigStatus;
  requireLogin: boolean;
  auth: "unknown" | "anonymous" | "authenticated";
  plans: readonly unknown[] | null;
  plansFailed: boolean;
}): MembershipView {
  if (config === "error") return "config-error";
  if (config === "loading") return "loading";
  if (requireLogin && auth === "unknown") return "loading";
  if (requireLogin && auth !== "authenticated") return "gated";
  if (plansFailed) return "plans-error";
  if (!plans) return "loading";
  return plans.length === 0 ? "empty" : "plans";
}
